import { useState, useMemo, useEffect, useRef, useCallback } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  Loader2, Search, X, AlertTriangle, CheckCircle2, XCircle,
  Download, Shield, Save, FolderOpen, Trash2, RotateCcw, Link2,
  ChevronDown, Database, BarChart3,
} from 'lucide-react'
import { matchRateApi } from '@/api/client'
import { showToast } from '@/store/uiStore'
import { CountrySelect, COUNTRIES, getFlagEmoji } from './CountrySelect'

// ─── Saved presets ────────────────────────────────────────────────────

interface SavedPreset {
  id: string
  name: string
  countries: string[]
  ruleset: '1+1' | '2+2'
  sortPriority?: 'coverage' | 'production'
  addressRequired?: boolean
  results?: BulkResultRow[]
  createdAt: string
}

const PRESETS_KEY = 'dealhub:bulk-assessment-presets'

function loadPresets(): SavedPreset[] {
  try {
    const raw = localStorage.getItem(PRESETS_KEY)
    return raw ? JSON.parse(raw) : []
  } catch { return [] }
}

function savePresets(presets: SavedPreset[]) {
  localStorage.setItem(PRESETS_KEY, JSON.stringify(presets))
}

// Country list, CountrySelect, getFlagEmoji imported from ./CountrySelect

// ─── Types ────────────────────────────────────────────────────────────

interface BulkResultRow {
  country_code: string
  country_name: string
  ruleset: string
  achievable: boolean
  strength_label: string
  coverage_pct: number
  recommended_sources: { name: string; vendor: string; coverage: number; mandatory_pii: string[]; notes?: string | null; deal_sheet?: string | null; missing_deal_sheet?: boolean; pass_rate?: number | null; pass_rate_checks?: number | null }[]
  best_pass_rate: number | null
  best_pass_rate_source: string | null
  best_pass_rate_checks: number | null
  production_data_source?: 'benchmark' | 'all_customers' | null
  overlap_warning: boolean
  consent_warning: boolean
  warnings: string[]
  alt_ruleset?: string
  alt_achievable?: boolean
  alt_strength_label?: string
  alt_coverage_pct?: number
  alt_sources?: { name: string; vendor: string; coverage: number; mandatory_pii: string[]; notes?: string | null }[]
  expert_override?: {
    sources: { name: string; vendor: string; coverage?: number | null }[]
    two_plus_two_achievable: boolean
    one_plus_one_achievable: boolean
    strength: number
    summary: string | null
    curated_by: string | null
  } | null
}

function unwrap(res: any): any {
  return res?.data?.data || res?.data || null
}

function formatChecks(n: number): string {
  if (n >= 1000000) return `${(n / 1000000).toFixed(1).replace(/\.0$/, '')}M`
  if (n >= 1000) return `${(n / 1000).toFixed(1).replace(/\.0$/, '')}K`
  return String(n)
}

// ─── Results table ────────────────────────────────────────────────────

function rateColor(rate: number | null) {
  if (rate == null) return 'text-[var(--text-muted)]'
  if (rate >= 70) return 'text-emerald-400'
  if (rate >= 40) return 'text-amber-400'
  return 'text-red-400'
}

const STRENGTH_STYLE: Record<string, { text: string; bg: string }> = {
  strong: { text: 'text-emerald-400', bg: 'bg-emerald-500/15' },
  decent: { text: 'text-amber-400', bg: 'bg-amber-500/15' },
  weak: { text: 'text-orange-400', bg: 'bg-orange-500/15' },
  not_achievable: { text: 'text-red-400', bg: 'bg-red-500/15' },
}

type SortKey = 'strength' | 'coverage' | 'pass_rate'
type SortDir = 'asc' | 'desc'

const STRENGTH_ORDER: Record<string, number> = { strong: 3, decent: 2, weak: 1, not_achievable: 0 }

function ResultsTable({ results, ruleset, showAlternatives, sortPriority }: { results: BulkResultRow[]; ruleset: string; showAlternatives: boolean; sortPriority: 'coverage' | 'production' }) {
  const [sortKey, setSortKey] = useState<SortKey | null>(null)
  const [sortDir, setSortDir] = useState<SortDir>('desc')

  const is2plus2 = ruleset === '2+2'

  const getMinCoverage = (r: BulkResultRow): number => {
    if (r.recommended_sources.length === 0) return 0
    if (is2plus2 && r.recommended_sources.length >= 2) {
      return Math.min(...r.recommended_sources.map(s => s.coverage))
    }
    return r.coverage_pct
  }

  const toggleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir(d => d === 'desc' ? 'asc' : 'desc')
    } else {
      setSortKey(key)
      setSortDir('desc')
    }
  }

  const sortedResults = useMemo(() => {
    if (!sortKey) return results
    const arr = [...results]
    const mul = sortDir === 'desc' ? -1 : 1
    arr.sort((a, b) => {
      let va: number, vb: number
      switch (sortKey) {
        case 'strength':
          va = STRENGTH_ORDER[a.strength_label] ?? -1
          vb = STRENGTH_ORDER[b.strength_label] ?? -1
          break
        case 'coverage':
          va = is2plus2 ? getMinCoverage(a) : a.coverage_pct
          vb = is2plus2 ? getMinCoverage(b) : b.coverage_pct
          break
        case 'pass_rate':
          va = a.best_pass_rate ?? -1
          vb = b.best_pass_rate ?? -1
          break
      }
      return (va - vb) * mul
    })
    return arr
  }, [results, sortKey, sortDir, is2plus2])

  const SortableHeader = ({ label, sortField, align = 'left', tooltip }: { label: string; sortField: SortKey; align?: 'left' | 'right' | 'center'; tooltip?: string }) => {
    const iconRef = useRef<HTMLSpanElement>(null)
    const [tip, setTip] = useState<{ x: number; y: number } | null>(null)

    const showTip = useCallback(() => {
      if (!iconRef.current) return
      const rect = iconRef.current.getBoundingClientRect()
      setTip({ x: rect.left + rect.width / 2, y: rect.top })
    }, [])

    return (
      <th
        className={`px-2 py-2 font-medium cursor-pointer hover:text-[var(--text-secondary)] transition-colors select-none text-${align}`}
        onClick={() => toggleSort(sortField)}
      >
        <span className="inline-flex items-center gap-0.5">
          {label}
          {tooltip && (
            <>
              <span
                ref={iconRef}
                className="text-[8px] text-[var(--text-muted)] opacity-50 ml-0.5 cursor-help"
                onMouseEnter={showTip}
                onMouseLeave={() => setTip(null)}
                onClick={e => e.stopPropagation()}
              >
                ⓘ
              </span>
              {tip && (
                <div
                  className="fixed w-56 px-2.5 py-2 rounded-lg bg-zinc-900 border border-zinc-700 shadow-xl text-[10px] text-zinc-200 font-normal leading-relaxed whitespace-normal z-[9999] pointer-events-none"
                  style={{ left: tip.x, top: tip.y, transform: 'translate(-50%, calc(-100% - 8px))' }}
                >
                  {tooltip}
                </div>
              )}
            </>
          )}
          {sortKey === sortField ? (
            <span className="text-violet-400">{sortDir === 'desc' ? ' ↓' : ' ↑'}</span>
          ) : (
            <span className="opacity-30"> ↕</span>
          )}
        </span>
      </th>
    )
  }

  const exportCsv = () => {
    const baseHeaders = ['Country', 'Code', 'Achievable', 'Strength', is2plus2 ? 'Min Coverage %' : 'Coverage %', 'Sources', 'Source Coverages', 'Source Pass Rates', 'Vendors', 'Mandatory PII', 'Best Pass Rate', 'Best Pass Rate Checks', 'Best Source', 'Overlap Warning', 'Consent Warning', 'Warnings']
    const altHeaders = showAlternatives ? ['1+1 Achievable', '1+1 Strength', '1+1 Coverage %', '1+1 Source'] : []
    const headers = [...baseHeaders, ...altHeaders]
    const rows = sortedResults.map(r => {
      const base = [
        r.country_name,
        r.country_code,
        r.achievable ? 'Yes' : 'No',
        r.strength_label,
        is2plus2 ? getMinCoverage(r) : r.coverage_pct,
        r.recommended_sources.map(s => s.name).join('; '),
        r.recommended_sources.map(s => `${s.name}: ${s.coverage}%`).join('; '),
        r.recommended_sources.map(s => s.pass_rate != null ? `${s.name}: ${s.pass_rate}% (${s.pass_rate_checks ?? '?'} checks)` : `${s.name}: n/a`).join('; '),
        r.recommended_sources.map(s => s.vendor).join('; '),
        r.recommended_sources.flatMap(s => s.mandatory_pii).join('; '),
        r.best_pass_rate ?? '',
        r.best_pass_rate_checks ?? '',
        r.best_pass_rate_source ?? '',
        r.overlap_warning ? 'Yes' : '',
        r.consent_warning ? 'Yes' : '',
        r.warnings.join('; '),
      ]
      if (showAlternatives) {
        base.push(
          r.alt_achievable ? 'Yes' : 'No',
          r.alt_strength_label || '',
          String(r.alt_coverage_pct ?? ''),
          r.alt_sources?.map(s => s.name).join('; ') || '',
        )
      }
      return base
    })
    const csv = [headers, ...rows].map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n')
    const blob = new Blob([csv], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `bulk-${ruleset}-${new Date().toISOString().split('T')[0]}.csv`
    a.click()
    URL.revokeObjectURL(url)
  }

  const strong = results.filter(r => r.strength_label === 'strong')
  const decent = results.filter(r => r.strength_label === 'decent')
  const weak = results.filter(r => r.strength_label === 'weak')
  const notAchievable = results.filter(r => !r.achievable)

  return (
    <div className="space-y-3">
      {/* Summary bar */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <span className="text-sm font-semibold text-[var(--text-primary)]">{results.length} countries</span>
          {strong.length > 0 && <span className="text-xs text-emerald-400">{strong.length} strong</span>}
          {decent.length > 0 && <span className="text-xs text-amber-400">{decent.length} decent</span>}
          {weak.length > 0 && <span className="text-xs text-orange-400">{weak.length} weak</span>}
          {notAchievable.length > 0 && <span className="text-xs text-red-400">{notAchievable.length} not achievable</span>}
        </div>
        <button
          onClick={exportCsv}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg bg-[var(--bg-tertiary)] text-[var(--text-secondary)] hover:bg-[var(--bg-secondary)] transition-colors border border-[var(--border-color)]"
        >
          <Download size={12} /> Export CSV
        </button>
      </div>

      {/* Consent disclaimer */}
      <div className="flex items-start gap-2 p-2.5 rounded-lg bg-amber-500/5 border border-amber-500/20">
        <AlertTriangle size={14} className="text-amber-400 flex-shrink-0 mt-0.5" />
        <p className="text-[10px] text-amber-400/80">
          This bulk view does not consider consent requirements. Some sources may require consent from the individual being verified. Check the detailed view per country before confirming with the customer.
        </p>
      </div>

      {/* Table */}
      <div className="rounded-lg border border-[var(--border-color)] overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-[11px]">
            <thead>
              <tr className="bg-[var(--bg-secondary)] text-[var(--text-muted)] text-left">
                <th className="px-3 py-2 font-medium">Country</th>
                <SortableHeader label={ruleset} sortField="strength" align="center" />
                <SortableHeader label={is2plus2 ? 'Min. Coverage' : 'Coverage'} sortField="coverage" align="right" tooltip="% of adult population this source can look up (from internal KYC coverage sheet). Sources are ranked by this metric." />
                <th className="px-2 py-2 font-medium">
                  Recommended Sources
                  <span className="font-normal text-[var(--text-muted)]"> — by {sortPriority === 'production' ? 'prod. data' : 'coverage'}</span>
                </th>
                <th className="px-2 py-2 font-medium">Mandatory PII</th>
                <SortableHeader label="Prod. Pass Rate" sortField="pass_rate" align="right" tooltip="Actual entity-level pass rate from FrankieOne production traffic (ThoughtSpot). This may come from a different source than the recommended one." />
                {showAlternatives && <th className="px-2 py-2 font-medium text-center">1+1 Alt.</th>}
                <th className="px-2 py-2 font-medium text-center">Warnings</th>
              </tr>
            </thead>
            <tbody>
              {sortedResults.map((r, i) => {
                const ss = STRENGTH_STYLE[r.strength_label] || STRENGTH_STYLE.not_achievable
                const allPii = [...new Set(r.recommended_sources.flatMap(s => s.mandatory_pii))].sort()
                const hasWarnings = r.overlap_warning || r.consent_warning || r.warnings.length > 0
                const minCoverage = getMinCoverage(r)
                const displayCoverage = is2plus2 ? minCoverage : r.coverage_pct

                return (
                  <tr key={i} className="border-t border-[var(--border-color)]/50 hover:bg-[var(--bg-tertiary)]/30">
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-1">
                        <span className="mr-0.5">{getFlagEmoji(r.country_code)}</span>
                        <span className="font-medium text-[var(--text-primary)]">{r.country_name}</span>
                        {r.expert_override && (
                          <span
                            className="inline-flex items-center gap-0.5 text-[8px] px-1 py-0.5 rounded bg-amber-500/15 text-amber-400 border border-amber-500/25 font-medium shrink-0"
                            title={`Expert override: ${r.expert_override.summary || 'Curated by ' + (r.expert_override.curated_by || 'team')}`}
                          >
                            <svg width="8" height="8" viewBox="0 0 24 24" fill="currentColor" className="shrink-0"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg>
                            EXPERT
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-2 py-2 text-center">
                      <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-medium ${ss.bg} ${ss.text} border border-current/20`}>
                        {r.achievable ? r.strength_label : 'not achievable'}
                      </span>
                    </td>
                    <td className={`px-2 py-2 text-right font-bold ${displayCoverage > 0 ? rateColor(displayCoverage) : 'text-[var(--text-muted)]'}`}>
                      {displayCoverage > 0 ? `${displayCoverage}%` : '—'}
                    </td>
                    <td className="px-2 py-2">
                      <div className="flex flex-wrap gap-1">
                        {r.recommended_sources.map((s, j) => {
                          const reasons: string[] = [
                            `${s.name} (${s.vendor})`,
                            '',
                            'Why recommended:',
                            `• Highest coverage source at ${s.coverage}% of adult population`,
                            s.pass_rate != null && s.pass_rate_checks != null
                              ? `• Prod pass rate: ${s.pass_rate}% across ${formatChecks(s.pass_rate_checks)} checks`
                              : s.pass_rate != null
                                ? `• Prod pass rate: ${s.pass_rate}%`
                                : '• No production data available for this source',
                          ]
                          if (is2plus2 && j === 1) {
                            reasons[3] = `• Second independent source at ${s.coverage}% coverage`
                          }
                          if (s.missing_deal_sheet) {
                            reasons.push('', '⚠ Missing from F1 deal sheet — may need pricing')
                          } else if (s.deal_sheet) {
                            reasons.push(`• Deal sheet: ${s.deal_sheet}`)
                          }
                          if (s.notes) reasons.push('', `Note: ${s.notes}`)

                          return (
                            <span
                              key={j}
                              className={`text-[9px] px-1.5 py-0.5 rounded bg-[var(--bg-primary)] border text-[var(--text-secondary)] cursor-help ${
                                s.missing_deal_sheet ? 'border-amber-500/40' : 'border-[var(--border-color)]'
                              }`}
                              title={reasons.join('\n')}
                            >
                              {s.name} <span className="text-[var(--text-muted)]">({s.vendor})</span>
                              {is2plus2 && <span className={`ml-1 font-semibold ${rateColor(s.coverage)}`}>{s.coverage}%</span>}
                              {s.pass_rate != null && (
                                <span className={`ml-1 ${rateColor(s.pass_rate)}`}>
                                  {s.pass_rate}%
                                  {s.pass_rate_checks != null && <span className="text-[var(--text-muted)]">/{formatChecks(s.pass_rate_checks)}</span>}
                                </span>
                              )}
                              {s.missing_deal_sheet && <span className="ml-1 text-amber-400">⚠</span>}
                            </span>
                          )
                        })}
                        {r.recommended_sources.length === 0 && <span className="text-[var(--text-muted)]">—</span>}
                      </div>
                    </td>
                    <td className="px-2 py-2">
                      <div className="flex flex-wrap gap-0.5">
                        {allPii.map((p, j) => (
                          <span key={j} className="text-[8px] px-1 py-0.5 rounded bg-violet-500/10 text-violet-300">{p}</span>
                        ))}
                        {allPii.length === 0 && <span className="text-[var(--text-muted)]">—</span>}
                      </div>
                    </td>
                    <td className="px-2 py-2 text-right">
                      {r.best_pass_rate != null ? (() => {
                        const recNames = r.recommended_sources.map(s => s.name)
                        const isSameSource = r.best_pass_rate_source && recNames.some(n =>
                          r.best_pass_rate_source!.toLowerCase().includes(n.toLowerCase()) ||
                          n.toLowerCase().includes(r.best_pass_rate_source!.toLowerCase())
                        )
                        return (
                          <div>
                            <span className={`font-bold ${rateColor(r.best_pass_rate)}`}>{r.best_pass_rate}%</span>
                            {r.production_data_source === 'benchmark' && (
                              <span className="ml-1 text-[7px] px-1 py-0.5 rounded bg-violet-500/15 text-violet-400 font-medium align-middle">BM</span>
                            )}
                            {r.best_pass_rate_checks != null && (
                              <div className={`text-[8px] ${r.best_pass_rate_checks < 50 ? 'text-amber-400' : 'text-[var(--text-muted)]'}`}>
                                {formatChecks(r.best_pass_rate_checks)} checks
                              </div>
                            )}
                            {r.best_pass_rate_source && (
                              <div className={`text-[8px] ${isSameSource ? 'text-[var(--text-muted)]' : 'text-cyan-400'}`}>
                                {!isSameSource && <span className="mr-0.5">via</span>}
                                {r.best_pass_rate_source}
                              </div>
                            )}
                          </div>
                        )
                      })() : (
                        <span className="text-[var(--text-muted)]">—</span>
                      )}
                    </td>
                    {showAlternatives && (
                      <td className="px-2 py-2 text-center">
                        {r.alt_achievable ? (() => {
                          const altSs = STRENGTH_STYLE[r.alt_strength_label || ''] || STRENGTH_STYLE.not_achievable
                          return (
                            <div>
                              <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-medium ${altSs.bg} ${altSs.text} border border-current/20`}>
                                {r.alt_strength_label}
                              </span>
                              {r.alt_coverage_pct != null && (
                                <div className={`text-[9px] mt-0.5 font-semibold ${rateColor(r.alt_coverage_pct)}`}>{r.alt_coverage_pct}%</div>
                              )}
                              {r.alt_sources && r.alt_sources.length > 0 && (
                                <div className="text-[8px] text-[var(--text-muted)] mt-0.5">{r.alt_sources[0].name}</div>
                              )}
                            </div>
                          )
                        })() : (
                          <XCircle size={12} className="inline text-red-400/40" />
                        )}
                      </td>
                    )}
                    <td className="px-2 py-2 text-center">
                      {hasWarnings ? (
                        <div className="flex flex-col items-center gap-0.5">
                          {r.overlap_warning && (
                            <span className="text-[8px] px-1.5 py-0.5 rounded bg-amber-500/10 text-amber-400 whitespace-nowrap">overlap risk</span>
                          )}
                          {r.consent_warning && (
                            <span className="text-[8px] px-1.5 py-0.5 rounded bg-amber-500/10 text-amber-400 whitespace-nowrap">consent req</span>
                          )}
                          {r.warnings.length > 0 && (
                            <span className="text-[8px] px-1.5 py-0.5 rounded bg-red-500/10 text-red-400 whitespace-nowrap" title={r.warnings.join('\n')}>
                              {r.warnings.length} alert{r.warnings.length !== 1 ? 's' : ''}
                            </span>
                          )}
                        </div>
                      ) : (
                        <CheckCircle2 size={12} className="inline text-emerald-400/50" />
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}

// ─── Methodology Panel ───────────────────────────────────────────────

function MethodologyPanel({ ruleset, sortPriority, addressRequired }: { ruleset: string; sortPriority: 'coverage' | 'production'; addressRequired: boolean }) {
  const [open, setOpen] = useState(false)

  return (
    <div className="rounded-lg border border-[var(--border-color)] bg-[var(--bg-secondary)] overflow-hidden">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-[var(--bg-tertiary)] transition-colors"
      >
        <div className="flex items-center gap-2">
          <Database size={14} className="text-[var(--text-muted)]" />
          <span className="text-xs font-medium text-[var(--text-secondary)]">Data Sources & Methodology</span>
        </div>
        <ChevronDown size={14} className={`text-[var(--text-muted)] transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="px-4 pb-4 border-t border-[var(--border-color)] pt-3 space-y-4 text-xs text-[var(--text-secondary)]">
          <div>
            <div className="flex items-center gap-1.5 mb-1.5">
              <Database size={12} className="text-violet-400" />
              <span className="font-semibold text-[var(--text-primary)]">No AI is used in Bulk Assessment</span>
            </div>
            <p className="text-[var(--text-muted)] leading-relaxed">
              All results are computed deterministically from FrankieOne's internal databases and production data. No AI model is involved in the calculations.
            </p>
          </div>

          <div>
            <h4 className="font-semibold text-[var(--text-primary)] mb-1.5">Internal Coverage Database (D1)</h4>
            <p className="text-[var(--text-muted)] mb-2 leading-relaxed">
              The following tables are queried per country to build coverage profiles:
            </p>
            <div className="space-y-1.5 font-mono text-[10px]">
              <div className="p-2 rounded bg-[var(--bg-primary)] border border-[var(--border-color)]">
                <span className="text-violet-400">v_kyc_country</span>
                <span className="text-[var(--text-muted)] ml-2">— Country-level coverage flags, recommended vendors, match rates, and API status per data source</span>
              </div>
              <div className="p-2 rounded bg-[var(--bg-primary)] border border-[var(--border-color)]">
                <span className="text-violet-400">kyc_data_sources</span>
                <span className="text-[var(--text-muted)] ml-2">— Detailed source profiles: mandatory PII, consent requirements, Gov ID support, verifiable fields</span>
              </div>
              <div className="p-2 rounded bg-[var(--bg-primary)] border border-[var(--border-color)]">
                <span className="text-violet-400">vendor_address_details</span>
                <span className="text-[var(--text-muted)] ml-2">— Per-source address field verification (street, city, zip, state) with gap assessments</span>
              </div>
              <div className="p-2 rounded bg-[var(--bg-primary)] border border-[var(--border-color)]">
                <span className="text-violet-400">vendor_national_ids</span>
                <span className="text-[var(--text-muted)] ml-2">— Accepted national ID types per country with format patterns and regex validation</span>
              </div>
              <div className="p-2 rounded bg-[var(--bg-primary)] border border-[var(--border-color)]">
                <span className="text-violet-400">team_insights</span>
                <span className="text-[var(--text-muted)] ml-2">— Critical warnings and notes from the KYC product team (e.g. vendor limitations, known issues)</span>
              </div>
            </div>
          </div>

          <div>
            <div className="flex items-center gap-1.5 mb-1.5">
              <BarChart3 size={12} className="text-violet-400" />
              <span className="font-semibold text-[var(--text-primary)]">Production Pass Rates (ThoughtSpot)</span>
            </div>
            <p className="text-[var(--text-muted)] mb-2 leading-relaxed">
              Real entity-level KYC verification outcomes are fetched from ThoughtSpot production analytics.
              These are actual PASS/FAIL/REFER results from live FrankieOne customers across all accounts — not estimates.
            </p>
            <div className="space-y-1.5">
              <div className="p-2.5 rounded bg-[var(--bg-primary)] border border-[var(--border-color)]">
                <p className="font-mono text-[10px] text-violet-400 mb-1">Entity-level query (per country):</p>
                <pre className="text-[10px] text-[var(--text-muted)] whitespace-pre-wrap leading-relaxed">{`[Verification Class] = "KYC" [Country] = "<ISO3>" [Country] [Trans Status] [Created] >= "<time_bracket>" count [Entity Id]`}</pre>
              </div>
              <div className="p-2.5 rounded bg-[var(--bg-primary)] border border-[var(--border-color)]">
                <p className="font-mono text-[10px] text-violet-400 mb-1">Source-level query (per country):</p>
                <pre className="text-[10px] text-[var(--text-muted)] whitespace-pre-wrap leading-relaxed">{`[Verification Class] = "KYC" [Country] = "<ISO3>" [Country] [Provider Source] [Trans Status] [Created] >= "<time_bracket>" count [Entity Id]`}</pre>
              </div>
            </div>
            <p className="text-[var(--text-muted)] mt-2 leading-relaxed">
              <span className="font-medium text-[var(--text-secondary)]">Worksheet:</span>{' '}
              <span className="font-mono text-[10px]">f7d183a2-9693-412b-b78f-b4d0a4ac07b7</span> (KYC entity transactions).
              Time brackets: 1 month, 3 months, 12 months. Countries are queried individually for result quality.
            </p>
          </div>

          <div>
            <h4 className="font-semibold text-[var(--text-primary)] mb-1.5">How recommended sources are determined</h4>
            <p className="text-[var(--text-muted)] mb-2 leading-relaxed">
              The recommended source is simply <strong>the top-ranked source</strong> after sorting. The sort order depends on the ranking toggle:
            </p>
            <div className="space-y-2">
              <div className={`p-2.5 rounded border ${sortPriority === 'coverage' ? 'bg-violet-500/5 border-violet-400/20' : 'bg-[var(--bg-primary)] border-[var(--border-color)] opacity-60'}`}>
                <div className="font-semibold text-[var(--text-primary)] mb-1">
                  Coverage mode {sortPriority === 'coverage' && <span className="text-violet-400 font-normal">(active)</span>}
                </div>
                <ol className="list-decimal list-inside space-y-0.5 text-[var(--text-muted)]">
                  <li>Sort all available sources by <strong className="text-[var(--text-secondary)]">adult coverage %</strong> from the spreadsheet (highest first)</li>
                  <li>Tiebreaker: sources with production data are preferred over those without</li>
                  <li>The top source becomes the recommendation{ruleset === '2+2' && ' (top pair for 2+2)'}</li>
                </ol>
              </div>
              <div className={`p-2.5 rounded border ${sortPriority === 'production' ? 'bg-violet-500/5 border-violet-400/20' : 'bg-[var(--bg-primary)] border-[var(--border-color)] opacity-60'}`}>
                <div className="font-semibold text-[var(--text-primary)] mb-1">
                  Production mode {sortPriority === 'production' && <span className="text-violet-400 font-normal">(active)</span>}
                </div>
                <ol className="list-decimal list-inside space-y-0.5 text-[var(--text-muted)]">
                  <li>Sources <strong className="text-[var(--text-secondary)]">with production data</strong> float to the top; those without sink to the bottom</li>
                  <li>Among sources with data, sort by real ThoughtSpot pass rate (highest first)</li>
                  <li>Tiebreaker: coverage % from the spreadsheet</li>
                  <li>The top source becomes the recommendation{ruleset === '2+2' && ' (top pair for 2+2)'}</li>
                </ol>
              </div>
            </div>
            {ruleset === '2+2' && (
              <p className="text-[var(--text-muted)] mt-2 leading-relaxed">
                For 2+2: the tool picks the two highest-ranked sources with <em>different</em> names. If both come from the same vendor, an &ldquo;overlap risk&rdquo; warning is shown because they may share the same underlying database.
              </p>
            )}
            {addressRequired && (
              <p className="text-amber-400/80 mt-2 leading-relaxed">
                Address filter is active — sources without address verification capability are excluded before ranking.
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

// ─── Main component ───────────────────────────────────────────────────

export function BulkEnquiry() {
  const [searchParams] = useSearchParams()
  const [selectedCountries, setSelectedCountries] = useState<string[]>([])
  const [ruleset, setRuleset] = useState<'1+1' | '2+2'>('1+1')
  const [sortPriority, setSortPriority] = useState<'coverage' | 'production'>('coverage')
  const [addressRequired, setAddressRequired] = useState(false)
  const [preferCreditHeader, setPreferCreditHeader] = useState(false)
  const [loading, setLoading] = useState(false)
  const [progress, setProgress] = useState({ done: 0, total: 0, current: '' })
  const [results, setResults] = useState<BulkResultRow[] | null>(null)
  const [showAlternatives, setShowAlternatives] = useState(false)
  const [presets, setPresets] = useState<SavedPreset[]>([])
  const [showSaveDialog, setShowSaveDialog] = useState(false)
  const [presetName, setPresetName] = useState('')
  const [showPresets, setShowPresets] = useState(false)
  const [sharedAssessmentName, setSharedAssessmentName] = useState<string | null>(null)
  const [sharing, setSharing] = useState(false)

  useEffect(() => { setPresets(loadPresets()) }, [])

  // Load shared assessment from URL param
  useEffect(() => {
    const assessmentId = searchParams.get('assessment')
    if (!assessmentId) return
    const load = async () => {
      try {
        const res = await matchRateApi.loadAssessment(assessmentId)
        const data = (res as any)?.data?.data || (res as any)?.data
        if (!data) { showToast('Assessment not found', 'error'); return }
        setSelectedCountries(data.countries || [])
        setRuleset(data.ruleset as '1+1' | '2+2')
        setSortPriority((data.sort_priority as 'coverage' | 'production') || 'coverage')
        setAddressRequired(!!data.address_required)
        setSharedAssessmentName(data.name)
        if (data.results && data.results.length > 0) {
          setResults(data.results as BulkResultRow[])
          showToast(`Loaded "${data.name}" (${data.results.length} results)`, 'success')
        } else {
          showToast(`Loaded "${data.name}" — run to generate results`, 'success')
        }
      } catch {
        showToast('Failed to load shared assessment', 'error')
      }
    }
    load()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const handleShare = async () => {
    if (selectedCountries.length === 0) {
      showToast('Select countries first', 'error')
      return
    }
    setSharing(true)
    try {
      const name = sharedAssessmentName || presetName.trim() || `Assessment ${new Date().toLocaleDateString('en-AU', { day: 'numeric', month: 'short' })}`
      const res = await matchRateApi.saveAssessment({
        name,
        countries: selectedCountries,
        ruleset,
        sort_priority: sortPriority,
        address_required: addressRequired,
        results: results || undefined,
      })
      const inner = (res as any)?.data
      const payload = inner?.data || inner
      if (payload?.id) {
        const shareUrl = `${window.location.origin}/dashboard/presales/tools/match-rate?assessment=${payload.id}`
        await navigator.clipboard.writeText(shareUrl)
        setSharedAssessmentName(name)
        showToast('Link copied to clipboard', 'success')
      } else {
        showToast(inner?.error || 'Failed to save assessment', 'error')
      }
    } catch (err) {
      console.error('Share failed:', err)
      showToast('Failed to share assessment', 'error')
    }
    setSharing(false)
  }

  const handleSavePreset = () => {
    if (!presetName.trim() || selectedCountries.length === 0) return
    const preset: SavedPreset = {
      id: Date.now().toString(),
      name: presetName.trim(),
      countries: selectedCountries,
      ruleset,
      sortPriority,
      addressRequired,
      results: results || undefined,
      createdAt: new Date().toISOString(),
    }
    const updated = [preset, ...presets]
    setPresets(updated)
    savePresets(updated)
    setPresetName('')
    setShowSaveDialog(false)
    showToast(`Saved "${preset.name}"${results ? ' with results' : ''}`, 'success')
  }

  const handleLoadPreset = (preset: SavedPreset) => {
    setSelectedCountries(preset.countries)
    setRuleset(preset.ruleset)
    if (preset.sortPriority) setSortPriority(preset.sortPriority)
    if (preset.addressRequired !== undefined) setAddressRequired(preset.addressRequired)
    if (preset.results && preset.results.length > 0) {
      setResults(preset.results)
      showToast(`Loaded "${preset.name}" with saved results`, 'success')
    } else {
      showToast(`Loaded "${preset.name}"`, 'success')
    }
    setShowPresets(false)
  }

  const handleDeletePreset = (id: string) => {
    const updated = presets.filter(p => p.id !== id)
    setPresets(updated)
    savePresets(updated)
  }

  const handleRerunPreset = (preset: SavedPreset) => {
    setSelectedCountries(preset.countries)
    setRuleset(preset.ruleset)
    if (preset.sortPriority) setSortPriority(preset.sortPriority)
    if (preset.addressRequired !== undefined) setAddressRequired(preset.addressRequired)
    setResults(null)
    setShowPresets(false)
    // Trigger run after state update
    setTimeout(() => {
      const btn = document.getElementById('bulk-run-btn')
      if (btn) btn.click()
    }, 50)
  }

  const handleRun = async () => {
    if (selectedCountries.length === 0) {
      showToast('Select at least one country', 'error')
      return
    }
    setLoading(true)
    setResults(null)
    setProgress({ done: 0, total: selectedCountries.length, current: '' })

    // Process countries one at a time for better results
    const allResults: BulkResultRow[] = []
    let failed = 0
    for (let i = 0; i < selectedCountries.length; i++) {
      const code = selectedCountries[i]
      const countryName = COUNTRIES.find(c => c.code === code)?.name || code
      setProgress({ done: i, total: selectedCountries.length, current: countryName })
      try {
        const res = await matchRateApi.bulk({ country_codes: [code], ruleset, sort_priority: sortPriority, address_required: addressRequired, prefer_credit_header: preferCreditHeader })
        const data = unwrap(res)
        if (Array.isArray(data)) {
          allResults.push(...(data as BulkResultRow[]))
          setResults([...allResults])
        }
      } catch (err) {
        console.error(`Bulk failed for ${code}:`, err)
        failed++
      }
    }
    if (failed > 0) {
      showToast(`${failed} countr${failed === 1 ? 'y' : 'ies'} failed — check console`, 'error')
    }
    setProgress({ done: selectedCountries.length, total: selectedCountries.length, current: '' })
    setLoading(false)
  }

  return (
    <div className="space-y-4">
      {/* Setup */}
      <div className="rounded-lg border border-[var(--border-color)] bg-[var(--bg-secondary)] p-4 space-y-4">
        <div className="flex items-center justify-between mb-1">
          <div className="flex items-center gap-2">
            <Shield size={16} className="text-violet-400" />
            <h3 className="text-sm font-semibold text-[var(--text-primary)]">Bulk Country Assessment</h3>
            {sharedAssessmentName && (
              <span className="text-xs text-violet-400 bg-violet-500/10 px-2 py-0.5 rounded-full">{sharedAssessmentName}</span>
            )}
          </div>
          <div className="flex items-center gap-2">
            {/* Load saved preset */}
            <div className="relative">
              <button
                onClick={() => setShowPresets(!showPresets)}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg bg-[var(--bg-primary)] text-[var(--text-secondary)] hover:bg-[var(--bg-tertiary)] transition-colors border border-[var(--border-color)]"
              >
                <FolderOpen size={12} /> Saved ({presets.length})
              </button>
              {showPresets && (
                <>
                  <div className="fixed inset-0 z-10" onClick={() => setShowPresets(false)} />
                  <div className="absolute right-0 z-20 mt-1 w-80 max-h-64 overflow-auto rounded-lg border border-[var(--border-color)] bg-[var(--bg-secondary)] shadow-xl">
                    {presets.length === 0 ? (
                      <div className="px-4 py-6 text-center text-xs text-[var(--text-muted)]">
                        No saved assessments yet
                      </div>
                    ) : (
                      presets.map(p => (
                        <div key={p.id} className="flex items-center gap-2 px-3 py-2 border-b border-[var(--border-color)]/50 hover:bg-[var(--bg-tertiary)]/50 group">
                          <div className="flex-1 min-w-0 cursor-pointer" onClick={() => handleLoadPreset(p)}>
                            <div className="text-xs font-medium text-[var(--text-primary)] truncate">{p.name}</div>
                            <div className="text-[10px] text-[var(--text-muted)]">
                              {p.countries.length} countries • {p.ruleset} • {p.sortPriority === 'production' ? 'Prod' : 'Coverage'}
                              {p.addressRequired ? ' • Addr' : ''}
                              {' • '}{new Date(p.createdAt).toLocaleDateString('en-AU', { day: 'numeric', month: 'short' })}
                            </div>
                            {p.results && p.results.length > 0 && (
                              <div className="text-[10px] text-emerald-400">{p.results.length} saved results — click to load</div>
                            )}
                          </div>
                          <button
                            onClick={() => handleRerunPreset(p)}
                            className="p-1 rounded hover:bg-violet-500/20 text-[var(--text-muted)] hover:text-violet-400 transition-colors"
                            title="Regenerate (fresh data)"
                          >
                            <RotateCcw size={12} />
                          </button>
                          <button
                            onClick={() => handleDeletePreset(p.id)}
                            className="p-1 rounded hover:bg-red-500/20 text-[var(--text-muted)] hover:text-red-400 transition-colors opacity-0 group-hover:opacity-100"
                            title="Delete"
                          >
                            <Trash2 size={12} />
                          </button>
                        </div>
                      ))
                    )}
                  </div>
                </>
              )}
            </div>
            {/* Save current */}
            <button
              onClick={() => {
                if (selectedCountries.length === 0) {
                  showToast('Select countries first', 'error')
                  return
                }
                setShowSaveDialog(true)
              }}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg bg-[var(--bg-primary)] text-[var(--text-secondary)] hover:bg-[var(--bg-tertiary)] transition-colors border border-[var(--border-color)]"
            >
              <Save size={12} /> Save
            </button>
            {/* Share link */}
            <button
              onClick={handleShare}
              disabled={sharing || selectedCountries.length === 0}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg bg-violet-600 text-white hover:bg-violet-500 transition-colors disabled:opacity-50"
            >
              <Link2 size={12} /> {sharing ? 'Sharing...' : 'Share'}
            </button>
          </div>
        </div>

        {/* Save dialog */}
        {showSaveDialog && (
          <div className="flex items-center gap-2 p-2.5 rounded-lg bg-violet-500/5 border border-violet-500/20">
            <input
              autoFocus
              value={presetName}
              onChange={e => setPresetName(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') handleSavePreset(); if (e.key === 'Escape') setShowSaveDialog(false) }}
              placeholder="Assessment name (e.g. APAC 2+2)"
              className="flex-1 text-sm bg-[var(--bg-primary)] border border-[var(--border-color)] rounded-lg px-3 py-1.5 text-[var(--text-primary)] placeholder:text-[var(--text-muted)] outline-none focus:border-violet-500/50"
            />
            <button
              onClick={handleSavePreset}
              disabled={!presetName.trim()}
              className="px-3 py-1.5 text-xs rounded-lg bg-violet-600 text-white hover:bg-violet-500 transition-colors disabled:opacity-50"
            >
              Save
            </button>
            <button
              onClick={() => { setShowSaveDialog(false); setPresetName('') }}
              className="p-1.5 rounded-lg hover:bg-[var(--bg-tertiary)] text-[var(--text-muted)]"
            >
              <X size={14} />
            </button>
          </div>
        )}

        {/* Country select */}
        <div>
          <label className="text-xs font-medium text-[var(--text-secondary)] mb-1.5 block">Countries</label>
          <CountrySelect selected={selectedCountries} onChange={setSelectedCountries} />
          {selectedCountries.length > 0 && (
            <div className="text-[10px] text-[var(--text-muted)] mt-1">{selectedCountries.length} selected</div>
          )}
        </div>

        {/* Ruleset toggle + priority + run */}
        <div className="flex items-end gap-3 flex-wrap">
          <div>
            <label className="text-xs font-medium text-[var(--text-secondary)] mb-1.5 block">Ruleset</label>
            <div className="flex rounded-lg overflow-hidden border border-[var(--border-color)]">
              <button
                onClick={() => setRuleset('1+1')}
                className={`px-4 py-2 text-sm font-medium transition-colors ${
                  ruleset === '1+1' ? 'bg-violet-600 text-white' : 'bg-[var(--bg-primary)] text-[var(--text-secondary)] hover:bg-[var(--bg-tertiary)]'
                }`}
              >
                1+1
              </button>
              <button
                onClick={() => setRuleset('2+2')}
                className={`px-4 py-2 text-sm font-medium transition-colors border-l border-[var(--border-color)] ${
                  ruleset === '2+2' ? 'bg-violet-600 text-white' : 'bg-[var(--bg-primary)] text-[var(--text-secondary)] hover:bg-[var(--bg-tertiary)]'
                }`}
              >
                2+2
              </button>
            </div>
          </div>

          <div>
            <label className="text-xs font-medium text-[var(--text-secondary)] mb-1.5 block">Rank by</label>
            <div className="flex rounded-lg overflow-hidden border border-[var(--border-color)]">
              <button
                onClick={() => setSortPriority('coverage')}
                className={`px-3 py-2 text-xs font-medium transition-colors ${
                  sortPriority === 'coverage' ? 'bg-violet-600 text-white' : 'bg-[var(--bg-primary)] text-[var(--text-secondary)] hover:bg-[var(--bg-tertiary)]'
                }`}
                title="Rank sources by adult population coverage from the internal KYC sheet"
              >
                Coverage
              </button>
              <button
                onClick={() => setSortPriority('production')}
                className={`px-3 py-2 text-xs font-medium transition-colors border-l border-[var(--border-color)] ${
                  sortPriority === 'production' ? 'bg-violet-600 text-white' : 'bg-[var(--bg-primary)] text-[var(--text-secondary)] hover:bg-[var(--bg-tertiary)]'
                }`}
                title="Rank sources by real pass rate from FrankieOne production data (ThoughtSpot)"
              >
                Prod. Data
              </button>
            </div>
          </div>

          <div className="flex flex-col gap-1.5 pb-0.5">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={addressRequired}
                onChange={e => setAddressRequired(e.target.checked)}
                className="accent-violet-500"
              />
              <span className="text-xs text-[var(--text-secondary)]">Address validation required</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={preferCreditHeader}
                onChange={e => setPreferCreditHeader(e.target.checked)}
                className="accent-violet-500"
              />
              <span className="text-xs text-[var(--text-secondary)]">Prefer credit header sources</span>
            </label>
            {/* Show alternatives toggle (only relevant for 2+2) */}
            {ruleset === '2+2' && (
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={showAlternatives}
                  onChange={e => setShowAlternatives(e.target.checked)}
                  className="accent-violet-500"
                />
                <span className="text-xs text-[var(--text-secondary)]">Show 1+1 alternatives</span>
              </label>
            )}
          </div>

          <button
            id="bulk-run-btn"
            onClick={handleRun}
            disabled={loading || selectedCountries.length === 0}
            className="flex items-center gap-2 px-5 py-2 text-sm rounded-lg bg-violet-600 text-white hover:bg-violet-500 transition-colors disabled:opacity-50"
          >
            {loading ? (
              <><Loader2 size={14} className="animate-spin" /> Processing {selectedCountries.length} countries...</>
            ) : (
              <><Search size={14} /> Run Assessment</>
            )}
          </button>
        </div>
      </div>

      {/* Results */}
      {loading && (
        <div className="flex flex-col items-center py-8 gap-3">
          <Loader2 size={24} className="animate-spin text-violet-400" />
          <p className="text-sm text-[var(--text-muted)]">
            Processing {progress.current || '...'}
          </p>
          <div className="w-64 bg-[var(--bg-tertiary)] rounded-full h-2 overflow-hidden">
            <div
              className="h-full bg-violet-500 transition-all duration-300 rounded-full"
              style={{ width: `${progress.total > 0 ? (progress.done / progress.total) * 100 : 0}%` }}
            />
          </div>
          <p className="text-xs text-[var(--text-muted)]">
            {progress.done} / {progress.total} countries
          </p>
        </div>
      )}

      {results && <ResultsTable results={results} ruleset={ruleset} showAlternatives={showAlternatives && ruleset === '2+2'} sortPriority={sortPriority} />}

      {/* Data Sources & Methodology */}
      <MethodologyPanel ruleset={ruleset} sortPriority={sortPriority} addressRequired={addressRequired} />
    </div>
  )
}
